import type { EstimatedTimeNameRef } from '@gat-ship/types'
import { validateEtaSlot, type EtaFieldValue } from './eta-bridge'

/**
 * Resolves a picked estimated-time name to its `localReferenceTime`
 * (`HH:mm:ss`), or `undefined` when no name is selected or the name is not
 * in the registry. Pure lookup over the names list — the caller (shell)
 * passes the reactive registry value in.
 */
export function resolveReferenceTime(
  names: readonly EstimatedTimeNameRef[],
  name?: string
): string | undefined {
  if (!name) return undefined
  return names.find((e) => e.name === name)?.localReferenceTime
}

/**
 * Whether the ETA section as a whole is valid. The port ETA is always
 * checked; when the port captures a pilot-station ETA the pilot slot is
 * checked too. Mirrors the per-field {@link validateEtaSlot} rules: a slot
 * may be empty as long as its sibling is complete, but a partially-filled
 * slot is always rejected.
 */
export function isEtaSectionValid(params: {
  etaAtPort: EtaFieldValue
  etaAtPilotStation: EtaFieldValue
  pilotStationActive: boolean
}): boolean {
  const { etaAtPort, etaAtPilotStation, pilotStationActive } = params
  if (
    validateEtaSlot({
      value: etaAtPort,
      sibling: etaAtPilotStation,
      pilotStationActive
    })
  )
    return false
  if (
    pilotStationActive &&
    validateEtaSlot({
      value: etaAtPilotStation,
      sibling: etaAtPort,
      pilotStationActive
    })
  )
    return false
  return true
}

/**
 * Whether the port call can be created: every required string field holds a
 * value and the ETA section is valid. `etaSectionValid` is passed in (the
 * shell computes it from {@link isEtaSectionValid}).
 */
export function canSubmit(params: {
  values: {
    agencyId: string
    appointingPrincipal: string
    vesselImo: string
    callType: string
    portOfCallLocationCode: string
  }
  etaSectionValid: boolean
}): boolean {
  const { values, etaSectionValid } = params
  return (
    !!values.agencyId &&
    !!values.appointingPrincipal &&
    !!values.vesselImo &&
    !!values.callType &&
    !!values.portOfCallLocationCode &&
    etaSectionValid
  )
}
